import { createSlice } from '@reduxjs/toolkit';
import { createAsyncSaga } from 'create-async-saga';
import { set, get, merge } from 'lodash';
import { call, takeEvery, all, fork } from 'redux-saga/effects';
import { KEYS } from '../typed';
import {
  fetchData as fetchDataService,
  submitData as submitDataService,
  normalizeFetchData,
} from '../service';

const initialState = {};

export const fetchData = createAsyncSaga(
  '@table/fetchData',
  function* (request) {
    return yield call(fetchDataService, request);
  }
);

export function* watchFetchData() {
  yield takeEvery(fetchData.actionType, fetchData.asyncSaga);
}

export const preloadData = createAsyncSaga(
  '@table/preloadData',
  function* (requests) {
    return yield all(
      requests.map((request) => call(fetchDataService, request))
    );
  }
);

export function* watchPreloadData() {
  yield takeEvery(preloadData.actionType, preloadData.asyncSaga);
}

export const submitData = createAsyncSaga(
  '@table/submitData',
  function* (request) {
    return yield call(submitDataService, request);
  }
);

export function* watchSubmitData() {
  yield takeEvery(submitData.actionType, submitData.asyncSaga);
}

export const tableSlice = createSlice({
  name: '@table',
  initialState,
  reducers: {
    initializeValues: (state, action) => {
      if (action?.payload) {
        const {
          metadata: { table, key },
          options,
        } = action.payload;

        set(state, `${table}.${KEYS.DEFAULTVALUE}`, options);
        if (options.defaultValues) {
          set(
            state,
            `${table}.${KEYS.DATA}`,
            normalizeFetchData({ data: { data: options.defaultValues }, key })
          );
        }
      }
    },
    default: (state, action) => {
      if (action?.payload) {
        const {
          metadata: { table },
          data,
        } = action.payload;

        set(state, `${table}.${KEYS.DEFAULTVALUE}`, data);
        set(state, `${table}.${KEYS.DATA}`, data);
      }
    },
    rowUpdated(state, action) {
      const {
        rowData,
        rowKey,
        metadata: { table, key },
      } = action.payload;
      const existingRow = get(
        state[table].data.entities,
        `${rowKey}.${rowKey}`
      );
      merge(existingRow, rowData);
    },
    // rowAdded(state, action) {
    //   const {
    //     data,
    //     metadata: { table, key },
    //   } = action.payload;
    //   let data = state[table].data;

    // },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchData.fulfilled, (state, action) => {
      if (action?.payload) {
        const {
          data,
          metadata: { table },
        } = action.payload;
        set(state, `${table}.data`, data);
      }
    });
    builder.addCase(submitData.fulfilled, (state, action) => {
      if (action?.payload) {
        const {
          data,
          metadata: { table, name },
        } = action.payload;

        set(state, `${table}.commits.${name}`, {
          data,
          requestId: action?.meta?.requestId,
        });
      }
    });

    builder
      .addMatcher(
        (action) =>
          action.type.startsWith('@table') && action.type.endsWith('/pending'),
        (state, action) => {
          const table = get(action, 'meta.arg.metadata.table', undefined);
          if (table) {
            set(state, `${table}.${KEYS.LOADING}`, true);
          }
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith('@table') && action.type.endsWith('/rejected'),
        (state, action) => {
          const table = get(action, 'meta.arg.metadata.table', undefined);
          if (table) {
            set(state, `${table}.${KEYS.LOADING}`, false);
          }
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith('@table') &&
          action.type.endsWith('/fulfilled'),
        (state, action) => {
          const table = get(action, 'meta.arg.metadata.table', undefined);
          if (table) {
            set(state, `${table}.${KEYS.LOADING}`, false);
          }
        }
      );
  },
});

export const { initializeValues } = tableSlice.actions;

export function* tableSaga() {
  yield all([
    fork(watchFetchData),
    fork(watchPreloadData),
    fork(watchSubmitData),
  ]);
}

export default tableSlice.reducer;
